/**
 * Title Strategy - Optimized Version
 * 
 * This strategy handles the title topic by generating summary messages instead of
 * serializing entire components. It only caches excluded titles and shows a snapshot
 * of titles in the UI component.
 */

import { runAIMatches, runMatchesAnalyze } from '../api';
import React from 'react';
import { TitleOptions } from './title/components';
import generateEnhancedGuidance from './enhancedGuidance';
import { createBaseStrategy, StrategyPhase } from './baseStrategy';

// Track if the user has rejected title suggestions
let userRejectedTitleSuggestions = false;

// Cache for excluded titles (only store what's excluded, not all titles)
let excludedTitlesCache = [];

/**
 * Generate title summary message with included/excluded titles
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @param {Object} decision - Optional decision object that may contain handleRefreshResults
 * @returns {Object} Object containing summary message and title data
 */
const generateTitleSummaryMessage = async (messages, setMessages, decision = null) => {
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating title analysis...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // First get the regular AI matches to extract requirements
  const regularMatches = await runAIMatches(messages);
  
  // Update match results in the sidebar if handleRefreshResults is provided
  if (setMessages && decision && decision.handleRefreshResults) {
    console.log('Updating match results in sidebar with handleRefreshResults');
    decision.handleRefreshResults(messages, regularMatches);
  }
  
  if (!regularMatches) {
    throw new Error('Failed to get AI matches');
  }
  
  // Extract requirements from regularMatches
  const requirements =
    regularMatches.search_gen_response &&
    regularMatches.search_gen_response.icp_requirements
      ? regularMatches.search_gen_response.icp_requirements
      : [];
  
  // Run matchesAnalyze to get title distribution data
  const matchesAnalyzeResult = await runMatchesAnalyze(requirements, {
    run_on_self_org: false
  });
  
  // Process title data if available
  if (matchesAnalyzeResult && matchesAnalyzeResult.analysis_info && matchesAnalyzeResult.analysis_info.title_map) {
    const titleMap = matchesAnalyzeResult.analysis_info.title_map;
    
    // Sort title keys by the number of profiles (array length) in descending order
    const allTitleKeys = Object.keys(titleMap)
      .sort((a, b) => {
        const aProfiles = Array.isArray(titleMap[a]) ? titleMap[a].length : 0;
        const bProfiles = Array.isArray(titleMap[b]) ? titleMap[b].length : 0;
        return bProfiles - aProfiles;
      });
    
    // Take only top 5 titles for UI display (snapshot)
    const topTitles = allTitleKeys.slice(0, 5);
    
    console.log('Title strategy: Found', allTitleKeys.length, 'total titles, showing top 5:', topTitles);
    
    // Generate summary message based on current excluded titles
    let summaryMessage = '';
    if (excludedTitlesCache.length === 0) {
      // No exclusions yet
      const titleList = topTitles.slice(0, 3).map(title => `"${title}"`).join(', ');
      summaryMessage = `Based on your requirements, I found relevant job titles including ${titleList} and others. Would you like to review and refine these title options?`;
    } else {
      // Some titles are excluded
      const includedTitles = topTitles.filter(title => !excludedTitlesCache.includes(title));
      const excludedInTop = topTitles.filter(title => excludedTitlesCache.includes(title));
      
      if (includedTitles.length > 0 && excludedInTop.length > 0) {
        const includeList = includedTitles.slice(0, 2).map(title => `"${title}"`).join(', ');
        const excludeList = excludedInTop.map(title => `"${title}"`).join(', ');
        summaryMessage = `Perfect! I'll include titles like ${includeList} and exclude ${excludeList} from the search.`;
      } else if (includedTitles.length > 0) {
        const includeList = includedTitles.slice(0, 3).map(title => `"${title}"`).join(', ');
        summaryMessage = `Great! I'll focus on titles like ${includeList} for the search.`;
      } else {
        summaryMessage = `I've noted your title preferences and will adjust the search accordingly.`;
      }
    }
    
    return {
      aiMatchesResult: regularMatches,
      matchesAnalyzeResult,
      summaryMessage,
      topTitles, // Only top 5 for UI
      allTitleKeys, // All titles for reference (not serialized)
      titleMap
    };
  }
  
  return {
    aiMatchesResult: regularMatches,
    matchesAnalyzeResult: null,
    summaryMessage: 'Let me help you refine the job title for this role.',
    topTitles: [],
    allTitleKeys: [],
    titleMap: {}
  };
};

/**
 * Handler for the init phase of the title strategy
 */
const initPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Title strategy: init phase');

  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nInitializing title discovery...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Check if the user has rejected title suggestions
  if (userRejectedTitleSuggestions || 
      (decision.actionType === 'USER_REJECTED_TITLE_SUGGESTIONS')) {
    
    console.log('User rejected title suggestions, sending to backend');
    userRejectedTitleSuggestions = true;
    
    const titleGuidance = `
IMPORTANT: The current topic is JOB TITLE. The user has rejected our suggested titles.

Focus on discovering what job titles would be more appropriate for this role:
1. Ask the user what kinds of job titles they are looking for
2. Explore different title options based on their feedback
3. Consider seniority level, specialization, and industry standards
4. Suggest alternative titles that might better reflect the role's responsibilities

After gathering sufficient information, summarize the title requirements clearly and ask if they're ready to move on to the next topic.
`;
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: titleGuidance,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
  
  try {
    // Generate title summary data
    const titleData = await generateTitleSummaryMessage(messages, setMessages, decision);
    
    // Check if we have title data
    if (titleData.topTitles.length > 0) {
      console.log('Title strategy: Showing title options with', titleData.topTitles.length, 'titles');
      
      // Return a decision that will render the title options component (lightweight)
      return {
        ...decision,
        actionType: 'RENDER_TITLE_OPTIONS',
        component: TitleOptions,
        aiMatchesResult: titleData.aiMatchesResult,
        strategyPhase: StrategyPhase.SUGGESTIONS,
        componentProps: {
          titleKeys: titleData.topTitles, // Only top 5 titles
          advanceToNextTopic: decision.advanceToNextTopic,
          setMessages,
          onSendText: decision.onSendText,
          messages,
          onTitlesSelected: (selectedTitles, confirmationData) => {
            // Cache only the excluded titles
            const excludedTitles = titleData.topTitles.filter(title => !selectedTitles.includes(title));
            excludedTitlesCache = [...excludedTitles];
            
            console.log('Titles selected:', selectedTitles.length, 'Excluded:', excludedTitles.length);
            
            // Send summary message to backend instead of serializing component
            return {
              ...decision,
              actionType: 'SEND_TITLE_SUMMARY',
              summaryMessage: confirmationData.message,
              selectedTitles,
              excludedTitles,
              confirmationData
            };
          }
        }
      };
    } else {
      // Fall back to conversation if no title data
      const defaultGuidance = `Focus on refining the job title for this role. Ask about the level of seniority, the team structure, and how this role fits into the organization.`;
      
      return {
        ...decision,
        actionType: 'SEND_TO_BACKEND',
        agentGuidance: defaultGuidance,
        strategyPhase: StrategyPhase.SUGGESTIONS
      };
    }
  } catch (error) {
    console.error('Error generating title data:', error);
    
    const defaultGuidance = `Focus on refining the job title for this role. Ask about the level of seniority, the team structure, and how this role fits into the organization.`;
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: defaultGuidance,
      strategyPhase: StrategyPhase.SUGGESTIONS
    };
  }
};

/**
 * Handler for the suggestions phase of the title strategy
 */
const suggestionsPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Title strategy: suggestions phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating title suggestions...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Check if the user has rejected title suggestions
  if (userRejectedTitleSuggestions || 
      (decision.actionType === 'USER_REJECTED_TITLE_SUGGESTIONS')) {
    
    console.log('User rejected title suggestions, sending to backend');
    userRejectedTitleSuggestions = true;
    
    const titleGuidance = `
IMPORTANT: The current topic is JOB TITLE. The user has rejected our suggested titles.

Focus on discovering what job titles would be more appropriate for this role:
1. Ask the user what kinds of job titles they are looking for
2. Explore different title options based on their feedback
3. Consider seniority level, specialization, and industry standards
4. Suggest alternative titles that might better reflect the role's responsibilities

After gathering sufficient information, summarize the title requirements clearly and ask if they're ready to move on to the next topic.
`;
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: titleGuidance,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
  
  try {
    // Generate title summary data
    const titleData = await generateTitleSummaryMessage(messages, setMessages, decision);
    
    // Check if we have title data
    if (titleData.topTitles.length > 0) {
      console.log('Title strategy (suggestions): Showing title options with', titleData.topTitles.length, 'titles');
      
      // Return a decision that will render the title options component (lightweight)
      return {
        ...decision,
        actionType: 'RENDER_TITLE_OPTIONS',
        component: TitleOptions,
        aiMatchesResult: titleData.aiMatchesResult,
        strategyPhase: StrategyPhase.CONVERSATION,
        componentProps: {
          titleKeys: titleData.topTitles, // Only top 5 titles
          advanceToNextTopic: decision.advanceToNextTopic,
          setMessages,
          onSendText: decision.onSendText,
          messages,
          onTitlesSelected: (selectedTitles, confirmationData) => {
            // Cache only the excluded titles
            const excludedTitles = titleData.topTitles.filter(title => !selectedTitles.includes(title));
            excludedTitlesCache = [...excludedTitles];
            
            console.log('Titles selected:', selectedTitles.length, 'Excluded:', excludedTitles.length);
            
            // Send summary message to backend instead of serializing component
            return {
              ...decision,
              actionType: 'SEND_TITLE_SUMMARY',
              summaryMessage: confirmationData.message,
              selectedTitles,
              excludedTitles,
              confirmationData
            };
          }
        }
      };
    } else {
      // Generate enhanced agent guidance
      const enhancedGuidance = await generateEnhancedGuidance(
        titleData.aiMatchesResult, 
        decision, 
        'title'
      );
      
      return {
        ...decision,
        actionType: 'SEND_WITH_GUIDANCE',
        agentGuidance: enhancedGuidance,
        aiMatchesResult: titleData.aiMatchesResult,
        strategyPhase: StrategyPhase.CONVERSATION
      };
    }
  } catch (error) {
    console.error('Error generating title suggestions:', error);
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for the conversation phase of the title strategy
 */
const conversationPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Title strategy: conversation phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nContinuing conversation about title...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Check if this is a user rejection action
  if (decision.actionType === 'USER_REJECTED_TITLE_SUGGESTIONS') {
    console.log('User rejected title suggestions, setting flag to true');
    userRejectedTitleSuggestions = true;
  }
  
  // Handle title summary action (when user confirms title selection)
  if (decision.actionType === 'SEND_TITLE_SUMMARY') {
    console.log('Sending title summary to backend');
    
    // Create guidance that includes the summary message and excluded titles info
    const titleSummaryGuidance = `
IMPORTANT: The user has just confirmed their title preferences.

TITLE SUMMARY: ${decision.summaryMessage}

EXCLUDED TITLES: ${decision.excludedTitles.length > 0 ? decision.excludedTitles.join(', ') : 'None'}
SELECTED TITLES: ${decision.selectedTitles.join(', ')}

Acknowledge their title selection and ask if they're ready to move on to the next topic (company and industry context).

Example response: "Perfect! I've noted your title preferences. ${decision.summaryMessage} Shall we move on to discussing the company and industry context for this role?"
`;
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: titleSummaryGuidance,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
  
  try {
    // Generate title summary data
    const titleData = await generateTitleSummaryMessage(messages, setMessages, decision);
    
    // Check if we have title data and should show options
    if (titleData.topTitles.length > 0 && !userRejectedTitleSuggestions) {
      console.log('Title strategy (conversation): Showing title options with', titleData.topTitles.length, 'titles');
      
      // Return a decision that will render the title options component (lightweight)
      return {
        ...decision,
        actionType: 'RENDER_TITLE_OPTIONS',
        component: TitleOptions,
        aiMatchesResult: titleData.aiMatchesResult,
        strategyPhase: StrategyPhase.CONVERSATION,
        componentProps: {
          titleKeys: titleData.topTitles, // Only top 5 titles
          advanceToNextTopic: decision.advanceToNextTopic,
          setMessages,
          onSendText: decision.onSendText,
          messages,
          onTitlesSelected: (selectedTitles, confirmationData) => {
            // Cache only the excluded titles
            const excludedTitles = titleData.topTitles.filter(title => !selectedTitles.includes(title));
            excludedTitlesCache = [...excludedTitles];
            
            console.log('Titles selected:', selectedTitles.length, 'Excluded:', excludedTitles.length);
            
            // Send summary message to backend instead of serializing component
            return {
              ...decision,
              actionType: 'SEND_TITLE_SUMMARY',
              summaryMessage: confirmationData.message,
              selectedTitles,
              excludedTitles,
              confirmationData
            };
          }
        }
      };
    } else {
      // Generate specific guidance for the title conversation phase
      const titleConversationGuidance = `
IMPORTANT: The current topic is JOB TITLE. Focus ONLY on discussing the job title for this role.

1. First, acknowledge any title information the user has provided and ask for more details:
   - What level of seniority does this title represent?
   - How does this role fit into the team structure?
   - Are there any alternative titles that might better reflect the responsibilities?

2. Then, explore other aspects of the title that might be relevant:
   - Is this a new role or an existing one?
   - How does this title compare to similar roles in the industry?
   - Are there any specific qualifications or certifications that should be reflected in the title?

3. After gathering sufficient information, summarize the title requirements clearly:
   - "Based on our conversation, it sounds like the most appropriate title would be [summarize title]..."

4. Finally, EXPLICITLY ask if they're ready to move on to the next topic:
   - "Does that title sound accurate for the role? If so, shall we move on to discussing company and industry context?"
   - "Have I captured the job title correctly? If yes, we can proceed to the next area."

DO NOT change the topic to discussing skills, experience, or any other topic until the user has EXPLICITLY confirmed they're ready to move on from the title discussion.
`;
  
      return {
        ...decision,
        actionType: 'SEND_TO_BACKEND',
        agentGuidance: titleConversationGuidance,
        strategyPhase: StrategyPhase.CONVERSATION
      };
    }
  } catch (error) {
    console.error('Error in title conversation phase:', error);
    
    const titleConversationGuidance = `
IMPORTANT: The current topic is JOB TITLE. Focus ONLY on discussing the job title for this role.

1. First, acknowledge any title information the user has provided and ask for more details:
   - What level of seniority does this title represent?
   - How does this role fit into the team structure?
   - Are there any alternative titles that might better reflect the responsibilities?

2. Then, explore other aspects of the title that might be relevant:
   - Is this a new role or an existing one?
   - How does this title compare to similar roles in the industry?
   - Are there any specific qualifications or certifications that should be reflected in the title?

3. After gathering sufficient information, summarize the title requirements clearly:
   - "Based on our conversation, it sounds like the most appropriate title would be [summarize title]..."

4. Finally, EXPLICITLY ask if they're ready to move on to the next topic:
   - "Does that title sound accurate for the role? If so, shall we move on to discussing company and industry context?"
   - "Have I captured the job title correctly? If yes, we can proceed to the next area."

DO NOT change the topic to discussing skills, experience, or any other topic until the user has EXPLICITLY confirmed they're ready to move on from the title discussion.
`;
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: titleConversationGuidance,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Title strategy using the phase-based approach
 */
const titleStrategy = createBaseStrategy({
  topicName: 'title',
  initPhaseHandler,
  suggestionsPhaseHandler,
  conversationPhaseHandler
});

export default titleStrategy;
